import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Check, Lock } from 'lucide-react'
import { toast } from 'sonner'
import { motion } from '@/components/framer-motion'
import { OnboardingUserType } from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { billingClient } from '@/lib/api/billing.client'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep } from '../flow.config'

const CARDS: Array<{
  id: OnboardingUserType
  title: string
  description: string
}> = [
  {
    id: OnboardingUserType.SDK_BUILDER,
    title:
      'I prefer to use Claude Code or other LLM to personalize to my company',
    description:
      "Choose this if you are a builder. You'll end with an API key and can immediately start using the Super Property Management Harness.",
  },
  {
    id: OnboardingUserType.STANDARD_USER,
    title: "I prefer to use Super's user interface",
    description:
      'Choose this if you want a simpler setup. No code or vibes required.',
  },
]

export function SetupStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { state, patch } = useOnboarding()
  const [selected, setSelected] = useState<OnboardingUserType>(
    state.userType ?? OnboardingUserType.STANDARD_USER,
  )
  const [pathLocked, setPathLocked] = useState<boolean>(false)

  // Once a card is saved on either path, the userType is committed. Fetch
  // the billing state on mount so we can render the picker as read-only
  // (server rejects any PATCH that tries to switch anyway).
  useEffect(() => {
    let cancelled = false
    billingClient
      .getSubscription()
      .then(sub => {
        if (cancelled) return
        setPathLocked(sub != null)
      })
      .catch(() => {
        // Silent failure — locked=false is the safe default here since the
        // server will still refuse a real switch attempt.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSelect = (userType: OnboardingUserType) => {
    if (pathLocked || userType === selected) return
    const previous = selected
    setSelected(userType)
    patch({ userType }).catch(() => {
      setSelected(previous)
      toast.error("Couldn't save your choice. Try again.")
    })
  }

  const handleContinue = () => {
    const current = findStepByRoute(location.pathname)
    const next = current ? nextStep(current, selected) : null
    if (next) navigate(next.route)
  }

  return (
    <div className="mx-auto flex max-w-[560px] flex-col gap-8">
      <div className="space-y-3 text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · SETUP
        </p>
        <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
          What describes you?
        </h1>
        <p className="text-muted-foreground text-sm">
          Setup will optimize your preferred way of interacting with Super.
        </p>
      </div>

      {pathLocked ? (
        <div className="border-border bg-muted/30 flex items-start gap-3 rounded-md border p-3">
          <Lock className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-muted-foreground text-xs">
            Your path is locked because a card is already on file. Contact
            support if you need to switch paths.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {CARDS.map(card => {
          const isSelected = selected === card.id
          const isDisabled = pathLocked && !isSelected
          return (
            <motion.button
              key={card.id}
              type="button"
              onClick={() => handleSelect(card.id)}
              disabled={isDisabled}
              whileTap={isDisabled ? undefined : { scale: 0.995 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className={`relative w-full rounded-md border p-5 text-left transition-colors ${
                isSelected
                  ? 'border-foreground bg-foreground/5'
                  : isDisabled
                    ? 'border-border cursor-not-allowed opacity-50'
                    : 'border-border hover:border-foreground/40'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition ${
                    isSelected
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-muted-foreground/40'
                  }`}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold leading-snug">
                    {card.title}
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>
            </motion.button>
          )
        })}
      </div>

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-setup"
        priority={10}
      >
        <div className="pointer-events-auto flex items-center gap-3">
          {!pathLocked ? (
            <span className="text-muted-foreground text-xs">
              You can switch until a card is saved.
            </span>
          ) : null}
          <Button onClick={handleContinue} size="sm">
            Start setup →
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}
